import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Default to light regardless of system preference. Only honor an explicit
    // user toggle stored in localStorage.
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("tabbaqa-theme") as Theme;
      if (saved === "light" || saved === "dark") return saved;
    }
    return "light";
  });

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    if (typeof window !== "undefined") {
      localStorage.setItem("tabbaqa-theme", newTheme);
      updateDocumentTheme(newTheme);
    }
  };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  const updateDocumentTheme = (currentTheme: Theme) => {
    if (typeof document !== "undefined") {
      const root = document.documentElement;
      if (currentTheme === "dark") {
        root.classList.add("dark");
      } else {
        root.classList.remove("dark");
      }
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      updateDocumentTheme(theme);
    }
  }, []);

  useEffect(() => {
    // Update when theme changes
    if (typeof window !== "undefined") {
      updateDocumentTheme(theme);
    }
  }, [theme]);

  const value: ThemeContextType = {
    theme,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
